<h2 id="modelo">1. O que o grafo é, exatamente</h2>

Cada linha de `transactions.csv` traz uma descrição, uma categoria e um valor. A ontologia
extraída por LLM acrescenta a essa linha as entidades citadas no texto: a empresa, o setor,
a cidade, o canal de pagamento. O grafo é o que sobra quando você olha só para essas
entidades e para quem aparece junto com quem.

<DiagramaLinhaGrafo />

Duas entidades se ligam quando aparecem na mesma descrição. Nada mais. Não há hierarquia,
não há herança, não há inferência: é co-ocorrência medida, e a força da ligação é a massa
que passou por ela.

### O grafo é esparso — e isso é informação

São <Stat k="entities" /> entidades distintas em <Stat k="descriptions" /> descrições, de
<Stat k="entity_types" /> tipos. Só <Stat k="edge_pct" /> das descrições geram aresta entre
entidades, porque o resto cita uma entidade só — ou nenhuma: <Stat k="no_entity_pct" /> das
linhas não tiveram nada extraído.

Um grafo esparso não é um grafo pobre. Significa que a maioria das transações é identificada
por um único fato — quem recebeu — e que os casos densos, com empresa, setor, cidade e canal
juntos, são exatamente os que valem inspeção.

<h2 id="niveis">2. Nem toda entidade serve para classificar</h2>

Medindo, para cada entidade que aparece em pelo menos <Stat k="purity_min_rows" /> descrições,
quanto do seu volume cai na categoria mais frequente, os <Stat k="entity_types" /> tipos se
separam em dois blocos com clareza incômoda.

<PurityTable />

O corte não foi desenhado à mão: é pureza mediana de 70%. Acima disso a entidade **identifica**
o que a transação foi; abaixo, ela apenas **descreve** como ou onde aconteceu.

### Uma exceção que parece sinal e não é

`canal = PIX` aparece em <Stat k="canal_pix_categories" /> categorias diferentes, e
`uf = SP` em <Stat k="uf_sp_categories" />. São as entidades mais frequentes da base e as
mais inúteis para classificar: dizem que houve um pagamento e onde, nunca do quê. Usar canal
como âncora de categoria é o erro mais fácil de cometer aqui.

<h2 id="classificar">3. Como usar isso para classificar</h2>

**Âncoras.** Uma empresa com pureza 100% em dezenas de linhas é uma regra de dicionário
esperando para ser escrita. É daí que sai a fila de trabalho por massa.

**Divergências.** Quando o setor de uma linha aparece em outra categoria em ≥70% dos casos,
a linha é sinalizada: <Stat k="flagged" /> delas hoje. Não significa que a categoria está
errada — significa que a evidência discorda dela e alguém precisa olhar.

**Ambiguidade real.** A mesma descrição em duas categorias diferentes é ambiguidade genuína
do extrato, não erro do classificador. `TRANSFERÊNCIA ENVIADA|FULANO` pode ser qualquer
coisa; sem declaração do usuário, o texto não decide.

**O teto do método.** Nenhuma quantidade de ontologia resolve um PIX de R$50 mil recebido de
empresa. O extrato não diz se é receita de PJ ou transferência entre contas próprias, e
adivinhar aqui custa a taxa de poupança inteira.

<h2 id="conhecer">4. O que o grafo responde além de categoria</h2>

Concentração de fornecedores, compromissos assumidos, rede de contrapartes, alcance
geográfico. Os parcelamentos declarados dão a forma do compromisso futuro:
<Stat k="installments_2_6" /> linhas parceladas em 2 a 6 vezes,
<Stat k="installments_7_12" /> em 7 a 12, <Stat k="installments_13_24" /> em 13 a 24 e
<Stat k="installments_25_60" /> acima disso. As últimas são financiamento com outro nome.

<h2 id="armadilhas">5. Seis armadilhas</h2>

1. **Tomar canal por categoria.** PIX é meio de pagamento, não tipo de gasto.
2. **Confiar em cidade.** Pureza mediana de 43%: diz onde, nunca o quê.
3. **Ler pureza sem olhar o n.** Uma entidade com 5 linhas e 100% de pureza é ruído.
4. **Somar MOVIMENTAÇÃO no total.** É o erro que este projeto existe para consertar.
5. **Tratar linha sinalizada como linha errada.** A sinalização diz que há divergência de
   evidência, não que a categoria está incorreta.
6. **Esquecer os homoglifos.** <Stat k="corrupted_descriptions" /> descrições da base trazem
   caractere cirílico na origem e viram entidades que parecem duplicatas mas não são.

<h2 id="rotina">6. Uma rotina que cabe em meia hora</h2>

1. Abra **Qualidade** e confirme que I4 reconcilia ao centavo. Se não reconcilia, pare aqui.
2. Em **Descrições**, filtre por severidade alta e ordene por prioridade de revisão.
3. Marque o que já foi olhado — a marca vai para o servidor e sobrevive a troca de máquina.
4. Em **Visão geral**, procure categorias com mediana baixa e um ponto muito acima: é um
   usuário atípico ou uma regra faltando.
5. Use a fila por massa para escolher a próxima regra de dicionário a escrever.
6. Rode `python3 -m tests.run` antes de aceitar qualquer regra nova no léxico.
